// app/src/lib/procurement/shoppingList.js — build the next order / shopping list.
//
// Supermarket trips (Costco, US Chef's Store, …) have no online ordering, so the
// app's job is to seed a checklist from what you usually buy there. Suggestions
// come from purchase history (line items grouped by normalized product name,
// most-frequently-bought first, with the last price paid). The in-progress list
// is persisted per vendor so it survives a reload between the restaurant and the
// store.

import { db } from '../db.js';
import { requireUserId } from './util.js';
import * as kv from '../idbKv.js';

const PACK_UNIT = /^(cs|case|cases|lb|lbs|oz|ct|cnt|count|ea|each|pc|pcs|pk|pkg|pack|kg|g|gr|gal|doz|dz|bx|box|qt|pt|ml|ltr|l|x|hash)$/;
function normItemName(x) {
  const s = (x || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  const toks = s.split(' ');
  let i = 0;
  while (i < toks.length && (/\d/.test(toks[i]) || PACK_UNIT.test(toks[i]))) i++;
  return toks.slice(i).join(' ').trim() || s;
}

/** Vendors you've bought from, with invoice counts (most-active first). */
export async function vendorsWithHistory() {
  const userId = await requireUserId();
  const invoices = (await db.invoices.where('userId').equals(userId).toArray()).filter((i) => !i.isDuplicate);
  const counts = {};
  for (const i of invoices) if (i.vendorId) counts[i.vendorId] = (counts[i.vendorId] || 0) + 1;
  const ids = Object.keys(counts);
  const vendors = ids.length ? await db.vendors.where('vendorId').anyOf(ids).toArray() : [];
  return vendors
    .map((v) => ({ vendorId: v.vendorId, name: v.name, invoiceCount: counts[v.vendorId] || 0, paymentMethod: v.paymentMethod }))
    .sort((a, b) => b.invoiceCount - a.invoiceCount || (a.name || '').localeCompare(b.name || ''));
}

/**
 * Items historically bought from a vendor, grouped by normalized name.
 * @returns {Promise<Array<{ key, name, lastPriceCents, uom, packSize, timesPurchased, lastDate }>>}
 */
export async function suggestionsForVendor(vendorId) {
  const userId = await requireUserId();
  const invoices = (await db.invoices.where('userId').equals(userId).toArray())
    .filter((i) => !i.isDuplicate && i.vendorId === vendorId);
  const dateOf = {};
  const invIds = new Set();
  invoices.forEach((i) => { dateOf[i.invoiceId] = i.issuedDate || ''; invIds.add(i.invoiceId); });
  const lines = (await db.lineItems.where('userId').equals(userId).toArray()).filter((l) => invIds.has(l.invoiceId));

  const byKey = {};
  for (const l of lines) {
    const key = normItemName(l.description);
    if (!key) continue;
    const d = dateOf[l.invoiceId] || '';
    const e = byKey[key] || { key, name: l.description, lastDate: '', lastPriceCents: 0, uom: l.uom, packSize: l.packSize, invoices: new Set() };
    e.invoices.add(l.invoiceId);
    if (d >= e.lastDate) { e.lastDate = d; e.name = l.description; e.lastPriceCents = l.unitPriceCents || 0; e.uom = l.uom; e.packSize = l.packSize; }
    byKey[key] = e;
  }
  return Object.values(byKey)
    .map((e) => ({ key: e.key, name: e.name, lastPriceCents: e.lastPriceCents, uom: e.uom, packSize: e.packSize, timesPurchased: e.invoices.size, lastDate: e.lastDate }))
    .sort((a, b) => b.timesPurchased - a.timesPurchased || (a.name || '').localeCompare(b.name || ''));
}

// ── Draft persistence (per user + vendor) ──────────────────────────────────
const draftKey = (userId, vendorId) => `shoppingList:${userId}:${vendorId}`;

/** @returns {Promise<{ qty:Record<string,number>, custom:Array<{key,name,lastPriceCents}>, updatedAt:number }|null>} */
export async function loadDraft(vendorId) {
  const userId = await requireUserId();
  return (await kv.get(draftKey(userId, vendorId))) || null;
}
export async function saveDraft(vendorId, draft) {
  const userId = await requireUserId();
  await kv.set(draftKey(userId, vendorId), { ...draft, updatedAt: Date.now() });
}
export async function clearDraft(vendorId) {
  const userId = await requireUserId();
  await kv.del(draftKey(userId, vendorId));
}
